import React, { useEffect, useRef } from 'react';
import img2 from '../assets/images/ts2.png';
import img5 from '../assets/images/ts5.png';
import img6 from '../assets/images/ts6.png';
import img7 from '../assets/images/ts7.png';
const TsunamiMeter = ({ intensity = 0, currentStreak = 0, streakDescription }) => {
  const rootRef = useRef(null);
  const setIntensityRef = useRef(() => {});
  const intensityValueRef = useRef(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const rainRoot = root.querySelector('#tw-rain');
    const intensityVal = root.querySelector('#tw-intensityVal');
    const badge = root.querySelector('#tw-badge');

    function setRain(n) {
      const current = rainRoot.children.length;
      const diff = n - current;
      if (diff > 0) {
        for (let i = 0; i < diff; i++) addDrop(true);
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) rainRoot.lastElementChild?.remove();
      }
    }

    function setAngle(deg) {
      root.style.setProperty('--rainAngle', deg + 'deg');
      const radians = (deg * Math.PI) / 180;
      const box = rainRoot.getBoundingClientRect();
      const travel = Math.max(box.width, box.height) * 1.6;
      const dx = -Math.sin(radians) * travel;
      const dyBase = Math.abs(Math.cos(radians)) * travel;
      const dy = Math.max(box.height + 80, dyBase);
      root.style.setProperty('--rainDriftX', dx + 'px');
      root.style.setProperty('--rainDriftY', dy + 'px');
      const tailLen = parseFloat(getComputedStyle(root).getPropertyValue('--rainTail')) || 60;
      const tailDx = Math.sin(radians) * tailLen;
      const tailDy = Math.cos(radians) * tailLen;
      root.style.setProperty('--rainTailX', tailDx + 'px');
      root.style.setProperty('--rainTailY', tailDy + 'px');
      root.style.setProperty('--spawnX', '0px');
      root.style.setProperty('--spawnY', '0px');
    }

    function setTail(len) {
      root.style.setProperty('--rainTail', len + 'px');
      const radians =
        (parseFloat(getComputedStyle(root).getPropertyValue('--rainAngle').replace('deg', '')) * Math.PI) / 180;
      const tailDx = Math.sin(radians) * len;
      const tailDy = Math.cos(radians) * len;
      root.style.setProperty('--rainTailX', tailDx + 'px');
      root.style.setProperty('--rainTailY', tailDy + 'px');
    }

    function setIntensity(v) {
      if (!rainRoot) return;
      const clamped = Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0;
      intensityValueRef.current = clamped;
      const t = Math.max(0, Math.min(1, clamped / 100));
      if (intensityVal) intensityVal.textContent = Math.round(t * 100) + '%';
      const level = Math.round(50 * t);
      root.style.setProperty('--level', level);
      const angle = 45 * t;
      setAngle(angle);
      const tail = Math.round(20 + 140 * t);
      setTail(tail);
      const drops = Math.round(600 * t);
      setRain(drops);
      const speedMul = 1 + 2 * t;
      root.style.setProperty('--speed', speedMul.toFixed(3));
      root.style.setProperty('--intensitySpeed', (1 + t).toFixed(3));
      for (const d of rainRoot.children) {
        d.style.animationDelay = `${-(Math.random() * 6).toFixed(2)}s`;
      }
      // Map intensity (0..100) to 4 discrete stages (1..4)
      // Thresholds: 0-24 = 1, 25-49 = 2, 50-74 = 3, 75-100 = 4
      let idx = 1;
      if (t >= 0.75) idx = 4;
      else if (t >= 0.5) idx = 3;
      else if (t >= 0.25) idx = 2;
      const imgs = [img2, img5, img6, img7];
      if (badge) badge.src = imgs[idx - 1];
      // Update status text and legend highlight
      const statusEl = root.querySelector('#tw-status');
      const legendEl = root.querySelector('#tw-legend');
      const stageNames = [
        'Resetting the Bankroll',
        'One-Day Heater',
        'Streak Swell',
        'Tsunami of Winning'
      ];
      const stageQuips = [
        'No streak yet—chalk it up and reload for the next slate.',
        'Fresh win logged. Keep stacking days to build momentum.',
        'Back-to-back cashes have the wave rising fast.',
        'Multiple days of big wins crashing in. It’s all profit surf now. 🌊💸'
      ];
      if (statusEl){
        statusEl.textContent = `Current Status: ${stageNames[idx-1]} — ${stageQuips[idx-1]}`;
      }
      if (legendEl){
        legendEl.querySelectorAll('.stage').forEach((el, i) => {
          if (i === idx - 1) el.classList.add('is-active');
          else el.classList.remove('is-active');
        });
      }
    }

    function addDrop(initial) {
      const d = document.createElement('div');
      d.className = 'drop';
      positionDrop(d, initial);
      d.addEventListener('animationiteration', () => positionDrop(d, false));
      rainRoot.appendChild(d);
    }

    function positionDrop(d, initial) {
      const box = rainRoot.getBoundingClientRect();
      const w = box.width,
        h = box.height;
      const cx = w * 0.5,
        cy = h * 0.5;
      const angleDeg = parseFloat(getComputedStyle(root).getPropertyValue('--rainAngle')) || 0;
      const a = (angleDeg * Math.PI) / 180;
      const ux = -Math.sin(a);
      const uy = Math.cos(a);
      const R = Math.min(w, h) * 0.5;
      const theta = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * R;
      let px = cx + r * Math.cos(theta);
      let py = cy + r * Math.sin(theta);
      const t = Math.max(0, Math.min(1, intensityValueRef.current / 100));
      const bias = R * (0.15 + 0.25 * t);
      px -= ux * bias;
      py -= uy * bias;
      px = Math.max(0, Math.min(w, px));
      py = Math.max(0, Math.min(h, py));
      const len = 40 + Math.random() * 120;
      const baseDur = 1.8 + Math.random() * 2.8;
      const negOffset = -(Math.random() * 6).toFixed(2);
      d.style.left = px + 'px';
      d.style.top = py + 'px';
      d.style.height = len + 'px';
      d.style.animationDuration = `calc(${baseDur}s / (var(--speed) * var(--intensitySpeed)))`;
      d.style.animationDelay = `${negOffset}s`;
    }

    setIntensityRef.current = setIntensity;
    setIntensity(intensity);

    return () => {
      setIntensityRef.current = () => {};
      if (rainRoot) {
        rainRoot.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    setIntensityRef.current(intensity);
  }, [intensity]);

  const streakText = (() => {
    if (typeof streakDescription === 'string') return streakDescription;
    if (currentStreak > 0) {
      return `${currentStreak} day${currentStreak === 1 ? '' : 's'} on a winning streak`;
    }
    return 'No active winning streak';
  })();

  return (
    <div className="tsunami-meter-card">
      <style>{`
        /* Scoped CSS for TsunamiMeter */
        #tsunami-widget{
          --bg:#0b1020;
          --ring:#c0c7d1;
          --size: 360px;           /* diameter of the circle */
          --level: 50;             /* 0..100 water level percentage */
          --speed: 1;              /* base speed multiplier */
          --rainAngle: -20deg;     /* tilt of rain */
          --rainDrift: 15vw;       /* horizontal drift per fall */
          --rainTail: 60px;        /* visible tail length */
          --rainDriftX: 20vw;      /* horizontal travel component */
          --rainDriftY: 120vh;     /* vertical travel component */
          --rainTailX: 0px;        /* horizontal tail offset */
          --rainTailY: 60px;       /* vertical tail offset */
          --spawnX: 0px;           /* starting offset along angle */
          --spawnY: 120px;         /* starting offset along angle */
          --intensitySpeed: 1;     /* scales rain speed with intensity */
        }
        #tsunami-widget *{ box-sizing: border-box; }
        #tsunami-widget{ display:grid; gap:1.5rem; justify-items:center; width:100%; }
        #tsunami-widget .wrap{ display:grid; gap:1rem; justify-items:center; }
        #tsunami-widget .water-round-container{
          position:relative; width:var(--size); height:var(--size);
          border-radius:50%; overflow:hidden; border:3px solid var(--ring);
          background: radial-gradient(circle at 50% 50%, #060b14 0%, #04080f 80%);
          box-shadow: 0 20px 60px #0006, inset 0 8px 30px #000a;
          contain: layout paint;
        }
        /* horizon glow */
        #tsunami-widget .glow{ position:absolute; inset:auto 0 26% 0; height:24%; pointer-events:none;
          background: radial-gradient(60% 120% at 50% 100%, #89e0ff22, transparent 70%);
        }
        /* waves group shifts vertically to set water level */
        #tsunami-widget .waves{
          position:absolute;
          left:50%;
          width:160%;
          height:160%;
          top: calc(120% - (var(--level) * 1.2%)); /* extend range so low really sits low */
          transform: translate(-50%, -28%); /* center horizontally, slightly larger/lower */
          z-index: 1;
        }
        /* Three layered blobs that rotate; alternate directions and speeds */
        #tsunami-widget .wave{ position:absolute; inset:0; border-radius:40%; opacity:.85; mix-blend-mode:screen; transform-origin:50% 50%; }
        #tsunami-widget .wave1{ background: hsl(199 95% 60% / .55); inset:-2% -2% -2% -2%; border-radius:45%; animation: rotL calc(7s/var(--speed)) linear infinite; }
        #tsunami-widget .wave2{ background: hsl(201 92% 55% / .40); inset:-8% -8% -8% -8%; border-radius:41%; animation: rotR calc(11s/var(--speed)) linear infinite; }
        #tsunami-widget .wave3{ background: hsl(210 88% 50% / .32); inset:-12% -12% -12% -12%; border-radius:38%; animation: rotL calc(15s/var(--speed)) linear infinite; }
        #tsunami-widget .wave4{ background: hsl(215 80% 45% / .24); inset:-16% -16% -16% -16%; border-radius:35%; animation: rotR calc(19s/var(--speed)) linear infinite; }
        @keyframes rotL{ to{ transform: rotate(-360deg); } }
        @keyframes rotR{ to{ transform: rotate(360deg); } }
        /* Rain lives inside the circle */
        #tsunami-widget .rain{ position:absolute; inset:0; pointer-events:none; z-index: 3; }
        #tsunami-widget .drop{
          position:absolute; width:2px; height: var(--rainTail); left:0; top:0;
          background: linear-gradient(to bottom, #ffffff00, #c9eeff);
          border-radius:1px; opacity:.7; filter: blur(.2px);
          animation: fall calc(2.1s / (var(--speed) * var(--intensitySpeed))) linear infinite;
          will-change: transform, opacity;
        }
        @keyframes fall{
          0%{ opacity: .7; transform: translate(0, 0) rotate(var(--rainAngle)); }
          100%{ opacity: .7; transform: translate(var(--rainDriftX), var(--rainDriftY)) rotate(var(--rainAngle)); }
        }
        /* Center badge image (level) above waves, below rain */
        #tsunami-widget .badge-img{ position:absolute; left:50%; top:50%; transform:translate(-50%, -50%); width:55%; max-width:220px; z-index:2; pointer-events:none; }
        #tsunami-widget .badge-img img{ display:block; width:100%; height:auto; }
        /* Controls */
        #tsunami-widget .panel{ display:flex; gap:1rem; flex-wrap:wrap; align-items:center; justify-content:center; background:#0b1220cc; border:1px solid #1b2a48; border-radius:14px; padding:.75rem 1rem; box-shadow: 0 10px 30px #0006, inset 0 1px 0 #ffffff0d; }
        #tsunami-widget .control{ display:flex; align-items:center; gap:.5rem; }
        #tsunami-widget .panel label{ font-size:.85rem; opacity:.9; }
        #tsunami-widget input[type='range']{ width:220px; accent-color:#4ec9ff; }
        #tsunami-widget .badge{ font-variant-numeric: tabular-nums; background:#0e1a30; border:1px solid #1f375f; padding:.2rem .45rem; border-radius:8px; }
        #tsunami-widget .title-arc{ position:absolute; inset:0; width:100%; height:100%; overflow:visible; pointer-events:none; }
        #tsunami-widget .title-arc text{ fill:#bfe8ff; font-weight:bold; text-transform:uppercase; }
        @media (max-width:560px){ #tsunami-widget{ --size: 300px; } #tsunami-widget input[type='range']{ width:150px; } }
        #tsunami-widget .legend-and-copy{ display:grid; gap:.5rem; justify-items:center; text-align:center; color:#cfe9ff; }
        #tsunami-widget .legend{ display:flex; gap:.5rem; flex-wrap:wrap; align-items:center; justify-content:center; font-size:.85rem; }
        #tsunami-widget .legend .stage{ padding:.25rem .5rem; border:1px solid #1f375f; border-radius:999px; background:#0e1a30; opacity:.7; transition:opacity .2s ease, transform .2s ease, box-shadow .2s ease; }
        #tsunami-widget .legend .stage.is-active{ opacity:1; box-shadow:0 0 0 1px #4ec9ff66, 0 4px 14px #0006; transform: translateY(-1px); }
        #tsunami-widget .status{ font-weight:600; color:#444; }
        #tsunami-widget .desc{ max-width:640px; margin:0; color:#c9def6; font-size:.95rem; line-height:1.45; }
      `}</style>
      <section id="tsunami-widget" ref={rootRef} aria-label="Tsunami wave and rain meter">
        <h2 className="tsunami-meter-title">TSUNAMI OF WINNING METER</h2>
        <p className="tsunami-meter-intro">
          This meter tracks Vegas Matt’s streaks. One win sparks a heater, back-to-back days create a swell, and sustained multi-day profits unleash the full Tsunami of Winning.
        </p>
        <div className="wrap">
          <div className="water-round-container">
            <svg
              className="title-arc"
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <path id="tw-arcPath" d="M 14 100 A 86 86 0 0 1 186 100" fill="none" />
              </defs>
              <text fontSize="12" fill="#bfe8ff" fontFamily="sans-serif" letterSpacing="1.8" dy="1">
                <textPath href="#tw-arcPath" startOffset="50%" textAnchor="middle">
                  TSUNAMI-OF-WINNING-O-METER
                </textPath>
              </text>
            </svg>
            <div className="glow" aria-hidden="true"></div>
            <div className="waves" id="tw-waves">
              <div className="wave wave1"></div>
              <div className="wave wave2"></div>
              <div className="wave wave3"></div>
              <div className="wave wave4"></div>
            </div>
            <div className="badge-img" aria-hidden="true">
              <img id="tw-badge" src={img2} alt="" />
            </div>
            <div className="rain" id="tw-rain" aria-hidden="true"></div>
          </div>
          <div className="legend-and-copy">
            <div id="tw-legend" className="legend">
              <span className="stage">Resetting the Bankroll</span>
              <span className="stage">One-Day Heater</span>
              <span className="stage">Streak Swell</span>
              <span className="stage">Tsunami of Winning</span>
            </div>
            <div id="tw-status" className="status">Current Status: Resetting the Bankroll — No streak yet—chalk it up and reload for the next slate.</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TsunamiMeter;
